import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { isCopilotConfigured } from '../../copilot_native/api/copilot-api';
import { onboardingPath, beginInitialOnboarding } from '../../copilot_native/model/onboarding';
import { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import Button from '../../../components/button/button';
import { createApp } from '../../../services/app/app-service';
import { appCreateNotify } from "../../../redux/app/app-actions";
import { Modal } from "flowbite-react";
import { HiOutlineSparkles, HiOutlineTemplate } from 'react-icons/hi';
import { IoIosClose } from "react-icons/io";
import { APP_MODES } from '../../../consts/common-consts';
import OnboardingWizard from "../onboarding";

const mapsStateToProps = (state, ownProps) => {
    return {};
}

const mapDispatchToProps = dispatch => {
    return {
        appCreateNotifyHandle: (appId) => {
            dispatch(appCreateNotify(appId));
        },
    };
}

const LABEL_CLASS = "mb-1.5 block text-sm font-medium text-gray-900";
const FIELD_CLASS = "block w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition focus:outline-none focus:ring-4";
const FIELD_OK_CLASS = "border-gray-200 hover:border-gray-300 focus:border-primary focus:ring-primary/10";
const FIELD_ERROR_CLASS = "border-rose-400 focus:border-rose-500 focus:ring-rose-500/10";

function Index({ onCompletion, onCancel, appCreateNotifyHandle, selectedTemplateProp, dismissible = true }) {
    const navigate = useNavigate();
    const rootRef = useRef(null);
    // flowbite's Modal re-appends its portal container on every render unless `root` is a
    // stable node, which drops input focus. The ref is null on the first render, so force
    // one re-render to anchor the modal before the user types (same as pages/playbooks).
    const [rootReady, setRootReady] = useState(false);
    useEffect(() => { if (rootRef.current) setRootReady(true); }, []);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        storeAddress: '',
        description: '',
        intentLess: true,
        templateId: null,
        appMode: APP_MODES.PROMPT_BASED,
        nluSettings: {
            provider: 'OpenAI',
            apiKey: ''
        }
    });
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const [error, setError] = useState({
        name: false,
        nluSettings: {
            apiKey: false
        }
    });

    const [formState, setFormState] = useState({
        processing: false,
        success: false,
        failed: false,
        message: ''
    });

    useEffect(() => {
        if (selectedTemplateProp) {
            setSelectedTemplate({ ...selectedTemplateProp })
        }
    }, [selectedTemplateProp]);

    const handleChange = event => {
        setFormData(previous => ({ ...previous, name: event.target.value }));
        setError(previous => ({ ...previous, name: false }));
    };

    const validateForm = () => {
        const valid = Boolean(formData.name.trim());
        setError(previous => ({ ...previous, name: !valid }));
        return valid;
    };

    const updateFormState = (processing, success, failed, message) => {
        setFormState(Object.assign({}, { processing, success, failed, message }))
    }

    const handleOnSubmit = async (event) => {
        event.preventDefault();

        if (formState.processing || !validateForm()) return;

        // Keep the legacy wizard for environments without the native Copilot.
        if (!isCopilotConfigured()) {
            setShowOnboarding(true);
            return;
        }

        updateFormState(true, false, false);
        try {
            const { response, status } = await createApp({
                ...formData,
                name: formData.name.trim(),
                templateId: selectedTemplate ? selectedTemplate.appId : null,
            });
            if (status !== 201 || !response?.appId) throw new Error('Application creation failed');
            appCreateNotifyHandle(response.appId);
            onCompletion();
            beginInitialOnboarding(response.appId);
            navigate(onboardingPath(response.appId), { state: { initialOnboardingAppId: response.appId } });
        } catch (_) {
            updateFormState(false, false, true, "Unable to create your application. Please try again.");
        }
    };

    // Show onboarding wizard as full-page overlay
    if (showOnboarding) {
        return <OnboardingWizard appName={formData.name} onCancel={() => setShowOnboarding(false)} />;
    }

    // Keep the stable Flowbite root outside transformed ancestors such as the sidebar.
    return createPortal(
        <div ref={rootRef}>
            <Modal show={rootReady} size="md" popup onClose={dismissible && !formState.processing ? onCancel : undefined} root={rootRef.current}
                aria-modal="true"
                aria-labelledby="create-application-title"
                theme={{ content: { base: "relative h-auto w-full p-4", inner: "relative flex max-h-[90dvh] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl" } }}>
                <Modal.Body className="p-6 sm:p-8">

                    <form onSubmit={handleOnSubmit} noValidate>
                        <div className="space-y-6">
                            <div className="flex items-start justify-between gap-4">
                                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-brand-lime dark:bg-brand-lime">
                                    <HiOutlineSparkles className="h-6 w-6" aria-hidden="true" />
                                </span>
                                {dismissible && <button type="button" aria-label="Close create application" onClick={onCancel} disabled={formState.processing} className="rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50">
                                    <IoIosClose className="h-6 w-6" />
                                </button>}
                            </div>
                            <div>
                                    <h3 id="create-application-title" className="text-xl tracking-tight font-semibold text-gray-900">Create new application</h3>
                                    <p className="mt-0.5 text-sm text-gray-500">
                                        Give your agent a name. You can change everything later.
                                    </p>
                            </div>
                            {formState.success && (
                                <div role="status" className="rounded-xl border border-emerald-100 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300">
                                    Application created. Taking you there…
                                </div>
                            )}
                            {formState.failed && (
                                <div role="alert" className="rounded-xl border border-rose-100 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
                                    {formState.message}
                                </div>
                            )}
                            <div>
                                <label htmlFor="name" className={LABEL_CLASS}>Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    autoFocus
                                    autoComplete="off"
                                    value={formData.name}
                                    placeholder="e.g. Customer Support Agent"
                                    onChange={handleChange}
                                    disabled={formState.processing}
                                    aria-describedby={error.name ? "application-name-error" : undefined}
                                    aria-invalid={error.name}
                                    className={`${FIELD_CLASS} ${error.name ? FIELD_ERROR_CLASS : FIELD_OK_CLASS}`}
                                />
                                {error.name && (
                                    <p id="application-name-error" role="alert" className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">Please enter a name for your application.</p>
                                )}
                            </div>
                            {selectedTemplate ?
                                <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white text-gray-700 shadow-sm">
                                        <HiOutlineTemplate className="h-5 w-5" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-semibold text-gray-900">
                                            {selectedTemplate.title}
                                        </p>
                                        <p className="truncate text-xs text-gray-500">
                                            Template · By {selectedTemplate.developer}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        aria-label="Remove template"
                                        onClick={() => setSelectedTemplate(null)}
                                        className="rounded-lg p-1 text-gray-400 transition hover:bg-gray-200 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    >
                                        <IoIosClose className="h-6 w-6" />
                                    </button>
                                </div>
                                :
                                <></>
                            }

                            <div className="flex flex-row justify-end gap-2 border-t border-gray-100 pt-5">
                                {dismissible && <Button type="button" color="default" text="Cancel" onClick={onCancel} disabled={formState.processing} />}
                                {formState.processing ?
                                    <Button type="button" text="Create application" loading={true} disabled={true} />
                                    :
                                    <Button type="submit" text={isCopilotConfigured() ? "Create application" : "Continue"} />
                                }

                            </div>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
        </div>,
        document.body
    )
}

export default connect(mapsStateToProps, mapDispatchToProps)(Index)
